// v0.5.9 Q4 — Refund Audit View. Renders the stored `refund_pairing`
// record for a credit note directly in the Review Queue drawer so the
// operator can see WHY a refund landed where it did: the pairing tier,
// the inherited channel + bucket, the validation verdict, and the full
// candidate list. Read-only; the data is stamped by engine.refund_pairing.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkSimple, CheckCircle, WarningCircle, Info } from "@phosphor-icons/react";
import { labelFor } from "../../lib/enumLabels";

// v0.6 — a Tier-3 refund with weak attribute-matching can legitimately
// have hundreds of "candidate originals" (every transaction sharing the
// same country/type/rate/currency). Rendering all of them inline turned
// the review queue into a wall of text with no way to actually read the
// row above it. Collapsed by default; the operator only needs the full
// list when actively investigating this specific row.
const CANDIDATES_PREVIEW_COUNT = 6;

const CHANNEL_LABEL = {
  OSS: "OSS return (correction)",
  NATIONAL: "National return (§17)",
  UNKNOWN: "Unresolved",
};

// v0.6 UX simplification — dropped the "Tier N" engine-internal framing;
// an operator doesn't need to know the matching engine has tiers, only
// how this particular refund got matched.
const TIER_LABEL = {
  1: "Matched by invoice number",
  2: "Matched by transaction details",
  3: "Assigned manually",
};

const VALIDATION_STYLE = {
  match: {
    cls: "text-emerald-700 dark:text-emerald-300",
    Icon: CheckCircle,
  },
  mismatch: {
    cls: "text-amber-700 dark:text-amber-300",
    Icon: WarningCircle,
  },
  manual: {
    cls: "text-slate-600 dark:text-slate-300",
    Icon: Info,
  },
  "n/a": {
    cls: "text-slate-500 dark:text-slate-400",
    Icon: Info,
  },
};

export const RefundAuditView = ({ pairing }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (!pairing) return null;

  const tier = pairing.tier;
  const channel = pairing.inherited_channel || "UNKNOWN";
  const bucket = pairing.inherited_bucket;
  const validation = pairing.validation || "n/a";
  const candidates = pairing.candidates || [];
  // A1-88: a saved record keeps the first 25 and says how many there were
  const count = pairing.candidate_count ?? candidates.length;
  const resolvedSpecific = !!pairing.resolved_specific;
  const vStyle = VALIDATION_STYLE[validation] || VALIDATION_STYLE["n/a"];
  const VIcon = vStyle.Icon;

  return (
    <div
      data-testid="refund-audit-view"
      className="mt-1.5 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-950/30 px-3 py-2 space-y-1.5 text-[10px]"
    >
      <div className="flex items-center gap-1.5 font-semibold text-teal-800 dark:text-teal-200 uppercase tracking-wide text-[9px]">
        <LinkSimple size={12} weight="bold" />
        {t("ui.review_grouped.refund_audit_title", { defaultValue: "Refund pairing audit" })}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-700 dark:text-slate-200">
        <span data-testid="refund-audit-tier">
          <span className="text-slate-500 dark:text-slate-400">
            {t("ui.review_grouped.refund_audit_tier", { defaultValue: "Pairing" })}:
          </span>{" "}
          <span className="font-semibold">
            {TIER_LABEL[tier] || "Unmatched"}
          </span>
        </span>

        {bucket && (
          <span data-testid="refund-audit-channel">
            <span className="text-slate-500 dark:text-slate-400">
              {t("ui.review_grouped.refund_audit_channel", { defaultValue: "Inherited channel" })}:
            </span>{" "}
            <span className="font-semibold">
              {CHANNEL_LABEL[channel] || channel}
            </span>
            <span
              className="ml-1 px-1 py-0.5 rounded bg-white/70 dark:bg-slate-800 border border-teal-200 dark:border-teal-800"
              title={labelFor("bucket", bucket)}
            >
              {labelFor("bucket", bucket)}
            </span>
          </span>
        )}
      </div>

      <div className={`flex items-center gap-1 ${vStyle.cls}`} data-testid="refund-audit-validation">
        <VIcon size={12} weight="bold" />
        <span>
          {t(`ui.review_grouped.refund_audit_validation_${validation.replace("/", "_")}`, {
            defaultValue:
              validation === "match"
                ? "Inherited channel confirmed by the routing table."
                : validation === "mismatch"
                  ? "Routing table would have landed a different channel — inheritance is authoritative."
                  : validation === "manual"
                    ? "Channel assigned manually by the operator."
                    : "Channel validation not applicable.",
          })}
        </span>
      </div>

      {candidates.length > 0 ? (
        <div data-testid="refund-audit-candidates" className="text-slate-600 dark:text-slate-300">
          <span className="text-slate-500 dark:text-slate-400">
            {resolvedSpecific
              ? t("ui.review_grouped.refund_audit_paired_to", { defaultValue: "Paired to" })
              : t("ui.review_grouped.refund_audit_candidates", { defaultValue: "Candidate originals" })}
            {" "}
            <span className="normal-case text-slate-400 dark:text-slate-500">
              ({count})
            </span>
            :
          </span>{" "}
          <span className="tabular-nums">
            {(expanded ? candidates : candidates.slice(0, CANDIDATES_PREVIEW_COUNT)).join(", ")}
            {expanded && count > candidates.length ? ", …" : ""}
          </span>
          {candidates.length > CANDIDATES_PREVIEW_COUNT && (
            <button
              type="button"
              data-testid="refund-audit-candidates-toggle"
              onClick={() => setExpanded((v) => !v)}
              className="ml-1 underline text-teal-700 dark:text-teal-300 hover:text-teal-800 dark:hover:text-teal-200"
            >
              {expanded
                ? t("ui.review_grouped.refund_audit_show_less", { defaultValue: "show less" })
                : t("ui.review_grouped.refund_audit_show_more", {
                    defaultValue: `+${candidates.length - CANDIDATES_PREVIEW_COUNT} more`,
                    count: candidates.length - CANDIDATES_PREVIEW_COUNT,
                  })}
            </button>
          )}
        </div>
      ) : (
        tier === 3 && validation !== "manual" && (
          <div data-testid="refund-audit-no-candidates" className="text-amber-700 dark:text-amber-300">
            {t("ui.review_grouped.refund_audit_no_candidates", {
              defaultValue: "No original supply matched — assign the channel below.",
            })}
          </div>
        )
      )}

      {pairing.note && (
        <div className="italic text-slate-500 dark:text-slate-400" data-testid="refund-audit-note">
          {pairing.note}
        </div>
      )}
    </div>
  );
};

export default RefundAuditView;
